import React, { Component, ErrorInfo, ReactNode, Suspense } from 'react';
import { isChunkLoadError, recoverFromChunkError, wasRecoveryAttempted } from '@/utils/chunkErrorRecovery';
import { RouteAwareLoadingScreen } from '@/components/loading/RouteAwareLoadingScreen';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  /** Fallback to show while retrying */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  isRecovering: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for handling chunk/dynamic import failures.
 * Shows existing loading skeleton during recovery, auto-retries once.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isRecovering: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> | null {
    if (isChunkLoadError(error)) {
      return { hasError: true, error };
    }
    // Let other errors bubble up to the main ErrorBoundary
    throw error;
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.log('[ChunkErrorBoundary] Caught error:', error.message);
    
    if (isChunkLoadError(error)) {
      this.attemptRecovery();
    }
  }

  private attemptRecovery = async () => {
    // Don't retry if already attempted this session
    if (wasRecoveryAttempted()) {
      logger.log('[ChunkErrorBoundary] Recovery already attempted, showing error');
      return;
    }

    this.setState({ isRecovering: true });

    // The recovery function will reload the page
    await recoverFromChunkError();
    
    // If we get here, recovery failed silently (no reload happened)
    this.setState({ isRecovering: false });
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, isRecovering: false });
  };

  public render() {
    const { hasError, isRecovering } = this.state;
    const { children, fallback } = this.props;

    if (hasError || isRecovering) {
      // Show loading skeleton during recovery - matches existing UI
      return fallback ?? <RouteAwareLoadingScreen />;
    }

    return children;
  }
}

/**
 * Wrap a Suspense fallback with chunk error handling
 */
export const ChunkSuspense: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => {
  return (
    <ChunkErrorBoundary fallback={fallback}>
      <Suspense fallback={fallback ?? <RouteAwareLoadingScreen />}>
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
};
